import * as THREE from "three";
import { RoundedBoxGeometry } from "three/addons/geometries/RoundedBoxGeometry.js";

export const PALETTE = {
  asphalt: 0x202a2b,
  sidewalk: 0x627071,
  stone: 0x7c8079,
  brick: 0x674942,
  glass: 0x274c55,
  teal: 0x2e8792,
  amber: 0xf2ca54,
  park: 0x355c48,
  water: 0x183f4c,
} as const;

export function roundedBox(
  width: number,
  height: number,
  depth: number,
  radius = 0.18,
): RoundedBoxGeometry {
  return new RoundedBoxGeometry(
    width,
    height,
    depth,
    2,
    Math.min(radius, width / 4, height / 4, depth / 4),
  );
}

export function standardMaterial(
  color: number,
  roughness = 0.82,
  metalness = 0.02,
  map?: THREE.Texture,
): THREE.MeshStandardMaterial {
  return new THREE.MeshStandardMaterial({
    color,
    roughness,
    metalness,
    ...(map ? { map } : {}),
  });
}

export function projectPlanarUvs(geometry: THREE.BufferGeometry): void {
  const position = geometry.getAttribute("position");
  const uv =
    geometry.getAttribute("uv") ??
    new THREE.BufferAttribute(new Float32Array(position.count * 2), 2);
  for (let index = 0; index < position.count; index += 1) {
    uv.setXY(index, position.getX(index), position.getY(index));
  }
  if (!geometry.getAttribute("uv")) geometry.setAttribute("uv", uv);
  uv.needsUpdate = true;
}

export function projectRoundedBoxUvs(geometry: THREE.BufferGeometry): void {
  const position = geometry.getAttribute("position");
  const uv = geometry.getAttribute("uv");
  const geometryIndex = geometry.getIndex();
  geometry.computeBoundingBox();
  const bounds = geometry.boundingBox;
  if (!bounds) return;
  const size = bounds.getSize(new THREE.Vector3());
  const projected = new THREE.Vector2();
  const project = (materialIndex: number, x: number, y: number, z: number): void => {
    switch (materialIndex) {
      case 0:
        projected.set(-z, y);
        break;
      case 1:
        projected.set(z, y);
        break;
      case 2:
        projected.set(x, -z);
        break;
      case 3:
        projected.set(x, z);
        break;
      case 4:
        projected.set(x, y);
        break;
      case 5:
        projected.set(-x, y);
        break;
    }
  };

  for (const group of geometry.groups) {
    const materialIndex = group.materialIndex ?? 0;
    let minU = Number.POSITIVE_INFINITY;
    let maxU = Number.NEGATIVE_INFINITY;
    let minV = Number.POSITIVE_INFINITY;
    let maxV = Number.NEGATIVE_INFINITY;
    for (let offset = group.start; offset < group.start + group.count; offset += 1) {
      const vertexIndex = geometryIndex?.getX(offset) ?? offset;
      project(
        materialIndex,
        position.getX(vertexIndex),
        position.getY(vertexIndex),
        position.getZ(vertexIndex),
      );
      minU = Math.min(minU, projected.x);
      maxU = Math.max(maxU, projected.x);
      minV = Math.min(minV, projected.y);
      maxV = Math.max(maxV, projected.y);
    }
    const targetU = materialIndex < 2 ? size.z : size.x;
    const targetV = materialIndex < 2 ? size.y : materialIndex < 4 ? size.z : size.y;
    const scaleU = targetU / (maxU - minU);
    const scaleV = targetV / (maxV - minV);
    for (let offset = group.start; offset < group.start + group.count; offset += 1) {
      const vertexIndex = geometryIndex?.getX(offset) ?? offset;
      project(
        materialIndex,
        position.getX(vertexIndex),
        position.getY(vertexIndex),
        position.getZ(vertexIndex),
      );
      uv.setXY(
        vertexIndex,
        (projected.x - minU) * scaleU - targetU / 2,
        (projected.y - minV) * scaleV - targetV / 2,
      );
    }
  }
  uv.needsUpdate = true;
}

export function disposeObject(
  root: THREE.Object3D,
  retainedMaterials?: ReadonlySet<THREE.Material>,
): void {
  const geometries = new Set<THREE.BufferGeometry>();
  const materials = new Set<THREE.Material>();
  root.traverse((object) => {
    if (!(object instanceof THREE.Mesh)) return;
    geometries.add(object.geometry);
    const meshMaterials = Array.isArray(object.material) ? object.material : [object.material];
    for (const material of meshMaterials) materials.add(material);
  });
  for (const geometry of geometries) geometry.dispose();
  for (const material of materials) {
    if (!retainedMaterials?.has(material)) material.dispose();
  }
}

export async function disposeObjectIncremental(
  root: THREE.Object3D,
  retainedMaterials: ReadonlySet<THREE.Material> | undefined,
  checkpoint: () => Promise<void>,
): Promise<void> {
  const geometries = new Set<THREE.BufferGeometry>();
  const materials = new Set<THREE.Material>();
  const stack: THREE.Object3D[] = [root];
  while (stack.length > 0) {
    const object = stack.pop()!;
    for (const child of object.children) stack.push(child);
    if (object instanceof THREE.Mesh) {
      geometries.add(object.geometry);
      const meshMaterials = Array.isArray(object.material) ? object.material : [object.material];
      for (const material of meshMaterials) materials.add(material);
    }
    await checkpoint();
  }
  root.clear();
  for (const geometry of geometries) {
    geometry.dispose();
    await checkpoint();
  }
  for (const material of materials) {
    if (!retainedMaterials?.has(material)) material.dispose();
    await checkpoint();
  }
}
